import React, { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "gcbc-theme";

/** Read saved theme; default to system preference, fall back to light. */
function getInitialTheme() {
  if (typeof window === "undefined") return "light";
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === "light" || saved === "dark") return saved;
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}

/**
 * useTheme — read + write the active theme.
 * Applies as `data-theme="dark|light"` on <html> so CSS vars in index.css
 * cascade through every Tailwind token (surface, ink, line, …).
 */
export function useTheme() {
  const [theme, setThemeState] = useState(getInitialTheme);

  // Sync DOM whenever theme changes. Briefly adds a `theme-flipping` class
  // on <html> so the body/main get a 220ms color transition during the flip
  // — pseudo-elements, gradients and backdrops snap instantly which kills
  // the previous flicker (they were transitioning at different rates).
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("theme-flipping");
    if (theme === "dark") root.setAttribute("data-theme", "dark");
    else root.removeAttribute("data-theme");
    localStorage.setItem(STORAGE_KEY, theme);
    const t = window.setTimeout(() => root.classList.remove("theme-flipping"), 260);
    return () => window.clearTimeout(t);
  }, [theme]);

  const setTheme = useCallback((t) => setThemeState(t), []);
  const toggleTheme = useCallback(
    () => setThemeState((cur) => (cur === "dark" ? "light" : "dark")),
    []
  );
  return { theme, setTheme, toggleTheme };
}

export default useTheme;
